import { LinearGradient } from "expo-linear-gradient";
import { useState, type PropsWithChildren } from "react";
import {
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle
} from "react-native";

import { Artwork } from "@/components/Artwork";
import { Muted, Overline, Text } from "@/design/Text";
import {
  contentMaxWidth,
  gradients,
  radius,
  RECITAL_COVER_ASPECT_RATIO,
  space
} from "@/design/tokens";

type RecitalHeroProps = PropsWithChildren<{
  artworkUri: string | null;
  fallbackSeed: string;
  artworkLabel: string;
  eyebrow: string;
  title: string;
  meta: string;
  progress?: { fraction: number; label: string };
  style?: StyleProp<ViewStyle>;
}>;

export function RecitalHero({
  artworkUri,
  fallbackSeed,
  artworkLabel,
  eyebrow,
  title,
  meta,
  progress,
  children,
  style
}: RecitalHeroProps) {
  const viewport = useWindowDimensions();
  const [width, setWidth] = useState(0);
  // Screen's content width is deterministic before the first native layout
  // event. Starting at `wide` made the desktop hero slide visibly onto phones
  // before onLayout corrected it one frame later.
  const estimatedWidth = Math.max(
    0,
    Math.min(viewport.width, contentMaxWidth(viewport.width)) -
      2 * (Platform.OS === "web" ? space.xl : space.lg)
  );
  const resolvedWidth = width || estimatedWidth;
  const wide = resolvedWidth >= 560;
  const progressWidth = `${Math.min(1, Math.max(0, progress?.fraction ?? 0)) * 100}%` as `${number}%`;

  const actions = children ? (
    <View style={[styles.actions, !wide && styles.actionsNarrow]}>{children}</View>
  ) : null;

  return (
    <View
      style={[styles.hero, style]}
      onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
    >
      <View style={styles.heroInner}>
        <Artwork
          uri={artworkUri}
          fallbackSeed={fallbackSeed}
          label={artworkLabel}
          style={[styles.poster, !wide && styles.posterNarrow]}
        />
        <View style={styles.heroBody}>
          <Overline>{eyebrow}</Overline>
          <Text variant={wide ? "hero" : "display"}>{title}</Text>
          <Muted variant="small" style={styles.heroMeta}>
            {meta}
          </Muted>
          {progress ? (
            <View style={styles.progressLine}>
              <View style={styles.progressTrack}>
                <LinearGradient
                  colors={gradients.playback as unknown as [string, string, ...string[]]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={[styles.progressFill, { width: progressWidth }]}
                />
              </View>
              <Muted variant="small" numberOfLines={1} style={styles.progressLabel}>
                {progress.label}
              </Muted>
            </View>
          ) : null}
          {wide ? actions : null}
        </View>
      </View>
      {!wide ? <View style={styles.actionsBelow}>{actions}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  hero: {
    marginTop: 0
  },
  heroInner: {
    alignItems: "center",
    flexDirection: "row",
    gap: 20
  },
  poster: {
    aspectRatio: RECITAL_COVER_ASPECT_RATIO,
    borderRadius: radius.md,
    width: 128
  },
  posterNarrow: {
    width: 84
  },
  heroBody: {
    flex: 1,
    gap: space.xs,
    minWidth: 0,
    width: "100%"
  },
  heroMeta: {
    marginTop: 2
  },
  progressLine: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm,
    marginTop: space.sm
  },
  progressTrack: {
    backgroundColor: "rgba(243, 244, 250, 0.22)",
    borderRadius: radius.pill,
    height: 3,
    overflow: "hidden",
    width: 64
  },
  progressFill: {
    height: 3
  },
  progressLabel: {
    flexShrink: 1
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.sm,
    marginTop: space.lg
  },
  actionsNarrow: {
    alignItems: "stretch",
    flexDirection: "column",
    flexWrap: "nowrap"
  },
  actionsBelow: {
    marginTop: space.lg
  }
});
